import React from "react";

import { productAnalytics, useNavigate, useTranslation } from "utils";

import { Button, Icon, MetaData, Tag, Typography } from "components";

import { OnboardingSteps } from "pages/app/scenario-onboarding/components/OnboardingSteps";

export type OnboardingFooterProps = {
  onBack: () => void;
  /** Disables Back when there is nowhere meaningful to go (e.g. step 1 once the landing is passed). */
  backDisabled?: boolean;
  /** Shown next to the CTA while the step is incomplete (e.g. "Add a field and crop rotation first"). */
  hint?: string;
  /** Renders the hint in the error colour (after a failed submit attempt). */
  hintIsError?: boolean;
  ctaLabel: string;
  onCtaClick: () => void;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  ctaTestId?: string;
};

type OnboardingLayoutProps = {
  stepIndex: number;
  pageName: string;
  /** Omit to hide the intro block entirely (e.g. while the calculating state is shown). */
  title?: string;
  subtitle?: string;
  /** Lifts the subtitle width cap so short subtitles stay on a single line (step 1). */
  wideSubtitle?: boolean;
  footer: OnboardingFooterProps;
  children: React.ReactNode;
};

// Full-screen wizard takeover for the get-started flow: covers the app shell (no sidebar,
// no default header), with its own pinned topbar (brand + step progress + close) and
// footer — only the body in between scrolls.
export const OnboardingLayout = ({ stepIndex, pageName, title, subtitle, wideSubtitle, footer, children }: OnboardingLayoutProps) => {
  const { t } = useTranslation(["scenarioAnalysis", "global", "meta"]);
  const navigate = useNavigate();

  return (
    <div className={`onboarding-overlay ${pageName}`}>
      {/* The overlay renders outside MainPage, so the tab title is set here rather than inherited. */}
      <MetaData title={t("meta:soilSight.title")} />
      <div className="onboarding">
        <div className="onboarding__topbar">
          <div className="onboarding__brand">
            <Typography variant="h4">{t("scenarioAnalysis:onboarding.brand")}</Typography>
            <Tag colour="yellow" text={t("scenarioAnalysis:onboarding.betaBadge")} />
          </div>
          <OnboardingSteps current={stepIndex} />
          <button
            type="button"
            className="onboarding__close"
            aria-label={t("global:close")}
            data-testid="onboarding-close"
            onClick={() => {
              productAnalytics.track("soilsights_onboarding:flow_cancel", { step_index: stepIndex + 1 });
              navigate("/scenarios-landing-page", { state: { fromOnboarding: true } });
            }}
          >
            <Icon iconName="tabler/x" size="m" />
          </button>
        </div>

        <div className="onboarding__body">
          {title && (
            <div className="onboarding__intro">
              <Typography variant="h2">{title}</Typography>
              {subtitle && (
                <Typography variant="paragraph-m" color="grey-600" className={`onboarding__intro-sub${wideSubtitle ? " onboarding__intro-sub--wide" : ""}`}>
                  {subtitle}
                </Typography>
              )}
            </div>
          )}

          {children}
        </div>

        <div className="onboarding__footer">
          <div className="onboarding__footer-inner">
            <Button type="text" dataTestId="onboarding-back-btn" disabled={footer.backDisabled} onClick={footer.onBack}>
              {t("scenarioAnalysis:onboarding.backButton")}
            </Button>
            <div className="onboarding__footer-actions">
              {footer.hint && (
                <Typography variant="paragraph-m" color={footer.hintIsError ? "red-500" : "grey-600"} className="onboarding__footer-hint">
                  {footer.hint}
                </Typography>
              )}
              <Button
                type="primary"
                dataTestId={footer.ctaTestId ?? "onboarding-cta"}
                disabled={footer.ctaDisabled}
                loading={footer.ctaLoading}
                onClick={footer.onCtaClick}
              >
                {footer.ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
